import React from 'react'
import { hydrate } from 'react-dom'
import { loadableReady } from '@loadable/component'
import { BrowserRouter } from 'react-router-dom'
import Cookies from 'universal-cookie'
import { CookieProvider } from 'lib/cookies'
import { ApiProvider, ApiClient } from 'jsonapi-react'
import App from 'app/App'
import { AppContext } from 'app/AppContext'
import ErrorPage from 'app/pages/ErrorPage'
import { Helmet, HelmetProvider } from 'react-helmet-async'
import createClient from './app/createClient'
import * as Sentry from '@sentry/react'
import { Integrations as TracingIntegrations } from '@sentry/tracing'
import { NotificationProvider } from 'app/theme/Notification'
import env from 'env'

if (process.env.NODE_ENV !== 'development') {
  Sentry.init({
    dsn: env.SENTRY_DSN,

    // This enables automatic instrumentation (highly recommended), but is not
    // necessary for purely manual usage
    integrations: [new TracingIntegrations.BrowserTracing()],

    // To set a uniform sample rate
    tracesSampleRate: env.SENTRY_TRACING_SAMPLE_RATE || 0.1,

    environment: env.APP_ENV,
    release: env.APP_VERSION,
    ignoreErrors: [
      'Object Not Found Matching Id'
    ],
    ignoreUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
    ]
  })
}

const render = () => {
  loadableReady(() => {
    const state = window.__APP_STATE__ || {}

    const client = createClient({ url: state.env.API_URL })
    client.hydrate(state.api)

    const loc = window.location
    const url = `${loc.origin}${loc.pathname === '/' ? '' : loc.pathname}`

    const appContext = state.app || {}

    const Root = (
      <Sentry.ErrorBoundary fallback={ErrorPage}>
        <CookieProvider value={new Cookies()}>
          <ApiProvider client={client}>
            <HelmetProvider>
              <NotificationProvider>
                <BrowserRouter>
                  <AppContext.Provider value={appContext}>
                    <App url={url} />
                  </AppContext.Provider>
                </BrowserRouter>
              </NotificationProvider>
            </HelmetProvider>
          </ApiProvider>
        </CookieProvider>
      </Sentry.ErrorBoundary>
    )

    hydrate(Root, document.getElementById('root'))
  })
}

const polyfills = []

if (!window.fetch || (!!window.MSInputMethodContext && !!document.documentMode)) {
  polyfills.push(import('react-app-polyfill/ie11'))
}

if (polyfills.length) {
  Promise.all(polyfills).then(render)
} else {
  render()
}

if (module.hot) {
  module.hot.accept()
}
